import { useEffect, useState } from 'react';
import { Fingerprint, Lock } from 'lucide-react';
import Button from './elements/Button';
import Input from './elements/Input';
import Info from './elements/Info';
import { getQuickUnlockConfig, getRemainingPinAttempts, verifyBiometric, verifyPin } from '../utils/quickUnlock';

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return '';
  const [name, domain] = email.split('@');
  if (!name || !domain) return email;
  if (name.length <= 2) return `**@${domain}`;
  return `${name.slice(0, 2)}***@${domain}`;
};

const QuickUnlockLockScreen = ({ email, onUnlocked, onUsePassword }) => {
  const config = getQuickUnlockConfig();
  const method = config?.method || 'pin';
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const attemptBiometric = async () => {
    setError(null);
    setIsVerifying(true);
    try {
      const success = await verifyBiometric();
      if (success) {
        onUnlocked();
      } else {
        setError('Biometric verification failed. Please try again.');
      }
    } catch {
      setError('Biometric verification was cancelled or failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (method === 'biometric') {
      attemptBiometric();
    }
  }, []);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsVerifying(true);
    try {
      const isValid = await verifyPin(pin);
      if (isValid) {
        onUnlocked();
        return;
      }
      const remaining = getRemainingPinAttempts();
      setError(`Incorrect PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
      setPin('');
    } catch {
      setError('Too many incorrect attempts. Please log in again.');
      setTimeout(onUsePassword, 1500);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-dark flex items-center justify-center p-4 font-sans antialiased">
      <div className="w-full max-w-md px-8 py-8 space-y-6 bg-card-background rounded-xl border border-accent-main/30 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent-main/10 border border-accent-main/20 mb-4">
            <Lock className="w-6 h-6 text-accent-main" />
          </span>
          <h2 className="text-2xl font-bold text-primary-dark">Welcome back</h2>
          {email && <p className="text-sm text-text-muted mt-1">{maskEmail(email)}</p>}
        </div>

        {error && <Info type="error">{error}</Info>}

        {method === 'biometric' ? (
          <div className="space-y-4">
            <Button type="primary" className="w-full" onClick={attemptBiometric} loading={isVerifying} disabled={isVerifying}>
              <Fingerprint className="w-5 h-5" /> Unlock with Face ID / Touch ID
            </Button>
          </div>
        ) : (
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <Input
              id="quickUnlockPinEntry"
              label="Enter your PIN"
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              disabled={isVerifying}
              autoFocus
            />
            <Button type="submit" className="w-full" loading={isVerifying} disabled={isVerifying || pin.length !== 6}>
              Unlock
            </Button>
          </form>
        )}

        <div className="text-center">
          <Button type="anchor" onClick={onUsePassword} disabled={isVerifying}>
            Use password instead
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickUnlockLockScreen;
